import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '@shopify/react-i18n';
import { useAppBridge } from '@shopify/app-bridge-react';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useCurrency } from '../../contexts/CurrencyContext';
import SalesChart from './components/SalesChart';
import enTranslations from './translations/en.json';
import frTranslations from './translations/fr.json';

const translationsByLocale = { en: enTranslations, fr: frTranslations };

const STATUS_BADGE = {
  confirmed: 'success',
  pending: 'caution',
  refunded: 'warning',
  cancelled: 'critical',
};

const TAB_STATUS = ['', 'pending', 'confirmed', 'cancelled', 'refunded'];

export default function Tracking() {
  const authFetch = useAuthFetch();
  const shopify = useAppBridge();
  const shop = shopify?.config?.shop ?? '';
  const [i18n] = useI18n({
    id: 'Tracking',
    fallback: enTranslations,
    translations(locale) {
      return translationsByLocale[locale] ?? enTranslations;
    },
  });

  // Data
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);
  const [isAffiliationActive, setIsAffiliationActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Confirmed-only overview stats
  const [confirmedRevenue, setConfirmedRevenue] = useState(0);
  const [confirmedConverted, setConfirmedConverted] = useState(0);
  const [confirmedCommission, setConfirmedCommission] = useState(0);

  // Filters & sort
  const [selectedTab, setSelectedTab] = useState(0);
  const [queryValue, setQueryValue] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [sortSelected, setSortSelected] = useState('createdAt desc');
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_SIZE = 25;
  const currency = useCurrency();

  const loadSales = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [sortField, sortDir] = (sortSelected ?? 'createdAt desc').split(' ');
    const params = new URLSearchParams({ limit: 500, sortField, sortDir });

    const tabStatus = TAB_STATUS[selectedTab];
    if (tabStatus) params.set('status', tabStatus);
    if (queryValue) params.set('search', queryValue);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (amountMin) params.set('amountMin', amountMin);
    if (amountMax) params.set('amountMax', amountMax);

    try {
      const res = await authFetch(`/api/sales?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setSales(data.sales);
      setTotal(data.total);
      setTotalRevenue(data.totalRevenue ?? 0);
      setTotalCommission(data.totalCommission ?? 0);
      setIsAffiliationActive(data.affiliationActive === true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, selectedTab, queryValue, dateFrom, dateTo, amountMin, amountMax, sortSelected]);

  const loadOverviewStats = useCallback(async () => {
    try {
      const res = await authFetch('/api/sales?status=confirmed&limit=1');
      if (!res.ok) return;
      const data = await res.json();
      setConfirmedRevenue(data.totalRevenue ?? 0);
      setConfirmedConverted(data.totalConverted ?? 0);
      setConfirmedCommission(data.totalCommission ?? 0);
      setIsAffiliationActive(data.affiliationActive === true);
    } catch (err) {
      console.error('Failed to load overview stats:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  useEffect(() => {
    loadOverviewStats();
  }, [loadOverviewStats]);

  // Reset to first page whenever filters or sort change
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedTab, queryValue, dateFrom, dateTo, amountMin, amountMax, sortSelected]);

  const hasPreviousPage = currentPage > 0;
  const hasNextPage = (currentPage + 1) * PAGE_SIZE < sales.length;
  const pagedSales = sales.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const paginationStart = sales.length === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const paginationEnd = Math.min((currentPage + 1) * PAGE_SIZE, sales.length);


  const handleSearch = useCallback((e) => {
    setQueryValue(e.currentTarget.value ?? '');
  }, []);

  const handleClearAll = useCallback(() => {
    setQueryValue('');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
  }, []);

  const formatRelativeDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const time = date.toLocaleTimeString(i18n.locale, { hour: '2-digit', minute: '2-digit', hour12: false });

    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) return i18n.translate('Tracking.date.today', { time });

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    if (isYesterday) return i18n.translate('Tracking.date.yesterday', { time });

    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    if (date > oneWeekAgo) {
      const weekday = date.toLocaleDateString(i18n.locale, { weekday: 'long' });
      return i18n.translate('Tracking.date.weekday', { weekday, time });
    }

    return date.toLocaleDateString(i18n.locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const tabs = [
    { label: i18n.translate('Tracking.tabs.all') },
    { label: i18n.translate('Tracking.tabs.pending') },
    { label: i18n.translate('Tracking.tabs.confirmed') },
    { label: i18n.translate('Tracking.tabs.cancelled') },
    { label: i18n.translate('Tracking.tabs.refunded') },
  ];

  return (
    <s-page heading={i18n.translate('Tracking.title')}>
      {error && (
        <s-banner
          tone="critical"
          heading={error}
          dismissible
          onDismiss={() => setError(null)}
        />
      )}

      {/* Tracked sales are a strict minimum — consent, ad blockers and
          cross-device purchases escape attribution. Links to the FAQ article. */}
      <s-banner
        tone="info"
        heading={i18n.translate('Tracking.minimumBanner.title')}
      >
        <s-stack direction="block" gap="base">
          <p>{i18n.translate('Tracking.minimumBanner.description')}</p>
          <s-link
            href="https://www.marques-de-france.fr/faq/ventes-fantomes-comment-marques-de-france-booste-votre-business-dans-lombre/"
            target="_blank"
          >
            {i18n.translate('Tracking.minimumBanner.link')}
          </s-link>
        </s-stack>
      </s-banner>

      {/* Overview cards */}
      <s-section>
        <style>{`
          .tracking-overview-grid {
            display: grid;
            gap: 16px;
          }
          .tracking-overview-grid-2 {
            grid-template-columns: 1fr 1fr;
          }
          .tracking-overview-grid-3 {
            grid-template-columns: 1fr 1fr 1fr;
          }
          @media (max-width: 768px) {
            .tracking-overview-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        <div className={`tracking-overview-grid ${isAffiliationActive ? 'tracking-overview-grid-3' : 'tracking-overview-grid-2'}`}>
          <s-section heading={i18n.translate('Tracking.revenue.title')}>
            <s-text>{confirmedRevenue.toFixed(2)} {currency}</s-text>
          </s-section>
          <s-section heading={i18n.translate('Tracking.converted.title')}>
            <s-text>{confirmedConverted}</s-text>
          </s-section>
          {isAffiliationActive && (
            <s-section heading={i18n.translate('Tracking.commission.title')}>
              <s-text>{confirmedCommission.toFixed(2)} {currency}</s-text>
            </s-section>
          )}
        </div>
      </s-section>

      {/* Analytics chart */}
      <SalesChart />

      {/* Filters */}
      <s-section>
        <s-stack direction="block" gap="base">
          {/* Tab buttons */}
          <s-stack direction="inline" gap="small">
            {tabs.map(({ label }, index) => (
              <s-button
                key={index}
                variant={selectedTab === index ? 'primary' : 'secondary'}
                onClick={() => setSelectedTab(index)}
              >
                {label}
              </s-button>
            ))}
          </s-stack>

          {/* Date + amount range filters */}
          <s-stack direction="inline" gap="small">
            <s-date-field
              label={i18n.translate('Tracking.filters.dateFrom')}
              placeholder={i18n.translate('Tracking.filters.datePlaceholder')}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.currentTarget.value)}
            />
            <s-date-field
              label={i18n.translate('Tracking.filters.dateTo')}
              placeholder={i18n.translate('Tracking.filters.datePlaceholder')}
              value={dateTo}
              onChange={(e) => setDateTo(e.currentTarget.value)}
            />
          </s-stack>
          <s-stack direction="inline" gap="small">
            <s-grid
              gridTemplateColumns="repeat(2, 1fr)"
              gap="small"
            >
              <s-money-field
                label={i18n.translate('Tracking.filters.amountMin')}
                value={amountMin}
                currency={currency}
                onChange={(e) => setAmountMin(e.currentTarget.value)}
              />
              <s-money-field
                label={i18n.translate('Tracking.filters.amountMax')}
                value={amountMax}
                currency={currency}
                onChange={(e) => setAmountMax(e.currentTarget.value)}
              />
            </s-grid>
          </s-stack>
          <s-stack direction="inline" gap="small">
            <s-button
              style={{ gridColumn: '1 / span 2' }}
              variant="secondary"
              onClick={handleClearAll}
            >
              {i18n.translate('Tracking.filters.clearAll')}
            </s-button>
          </s-stack>
        </s-stack>
      </s-section>

      {/* Sales table */}
      <s-section padding="none">
        {loading && sales.length === 0 ? (
          <s-stack direction="inline" justifyContent="center" padding="large">
            <s-spinner />
          </s-stack>
        ) : (
          <s-table
            loading={loading}
            paginate={sales.length > PAGE_SIZE}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            onNextpage={() => setCurrentPage((p) => p + 1)}
            onPreviouspage={() => setCurrentPage((p) => p - 1)}
          >
            <s-grid
              slot="filters"
              gap="small"
              gridTemplateColumns="1fr auto"
            >
              <s-text-field
                label={i18n.translate('Tracking.search.placeholder')}
                labelAccessibilityVisibility="exclusive"
                icon="search"
                placeholder={i18n.translate('Tracking.search.placeholder')}
                value={queryValue}
                onChange={handleSearch}
              />
              <s-stack direction="inline" gap="small">
                <s-button
                  icon="sort"
                  variant="secondary"
                  accessibilityLabel={i18n.translate('Tracking.sort.label')}
                  commandFor="tracking-sort-actions"
                />
              </s-stack>
              <s-popover id="tracking-sort-actions">
                <s-box padding="small">
                  <s-stack direction="block" gap="small">
                    {[
                      { value: 'createdAt desc', label: i18n.translate('Tracking.sort.dateDesc') },
                      { value: 'createdAt asc',  label: i18n.translate('Tracking.sort.dateAsc') },
                      { value: 'amount desc',    label: i18n.translate('Tracking.sort.amountDesc') },
                      { value: 'amount asc',     label: i18n.translate('Tracking.sort.amountAsc') },
                      { value: 'orderId asc',    label: i18n.translate('Tracking.sort.orderIdAsc') },
                      { value: 'orderId desc',   label: i18n.translate('Tracking.sort.orderIdDesc') },
                    ].map(({ value, label }) => (
                      <s-button
                        key={value}
                        variant={sortSelected === value ? 'primary' : 'tertiary'}
                        onClick={() => setSortSelected(value)}
                      >
                        {label}
                      </s-button>
                    ))}
                  </s-stack>
                </s-box>
              </s-popover>
            </s-grid>
            <s-table-header-row>
              <s-table-header listSlot="primary">
                {i18n.translate('Tracking.columns.shopifyOrderId')}
              </s-table-header>
              <s-table-header format="currency" listSlot="labeled">
                {i18n.translate('Tracking.columns.amount')}
              </s-table-header>
              {isAffiliationActive && (
                <s-table-header format="currency" listSlot="labeled">
                  {i18n.translate('Tracking.columns.commission')}
                </s-table-header>
              )}
              <s-table-header listSlot="secondary">
                {i18n.translate('Tracking.columns.status')}
              </s-table-header>
              <s-table-header listSlot="labeled">
                {i18n.translate('Tracking.columns.source')}
              </s-table-header>
              <s-table-header listSlot="labeled">
                {i18n.translate('Tracking.columns.date')}
              </s-table-header>
            </s-table-header-row>
            <s-table-body>
              {pagedSales.map((sale) => (
                <s-table-row key={sale.id}>
                  <s-table-cell>
                    <s-link
                      href={`https://${shop}/admin/orders/${sale.orderId}`}
                      target="_blank"
                    >
                      {sale.orderName || `#${sale.orderId}`}
                    </s-link>
                  </s-table-cell>
                  <s-table-cell>
                    {sale.amount.toFixed(2)} {sale.currency}
                  </s-table-cell>
                  {isAffiliationActive && (
                    <s-table-cell>
                      {sale.commissionAmount != null
                        ? `${sale.commissionAmount.toFixed(2)} ${sale.currency}`
                        : '—'}
                    </s-table-cell>
                  )}
                  <s-table-cell>
                    <s-badge tone={STATUS_BADGE[sale.status] ?? 'neutral'}>
                      {i18n.translate(`Tracking.status.${sale.status}`) || sale.status}
                    </s-badge>
                  </s-table-cell>
                  <s-table-cell>
                    <s-text>Marques de France</s-text>
                  </s-table-cell>
                  <s-table-cell>
                    <s-text tone="subdued">{formatRelativeDate(sale.createdAt)}</s-text>
                  </s-table-cell>
                </s-table-row>
              ))}
              {total > 0 && (
                <s-table-row>
                  <s-table-cell>
                    <strong>{total}</strong>
                  </s-table-cell>
                  <s-table-cell>
                    <strong>{totalRevenue.toFixed(2)} {currency}</strong>
                  </s-table-cell>
                  {isAffiliationActive && (
                    <s-table-cell>
                      <strong>{totalCommission.toFixed(2)} {currency}</strong>
                    </s-table-cell>
                  )}
                  <s-table-cell />
                  <s-table-cell />
                  <s-table-cell />
                </s-table-row>
              )}
            </s-table-body>
          </s-table>
        )}
      </s-section>
    </s-page>
  );
}

